import { Box, Button } from '@ff-it/ui';
import { useState } from 'react';
import type { ActivityType } from 'types';
import { actionErrorOrThrow } from 'utilities';
import { ClientDiscountTree, ClientRuleDialog, emptyRuleValues, formValuesFromRule } from '../discounts';
import type { ClientTargetPath } from '../discounts/types';
import { clientDiscountOwner } from '../discounts/owner';
import type { ClientAgreementVendorTerms, ClientDiscountRootOwner } from './types';
import { clientDiscountMutations } from './mutations';

type EditorSession = {
  kind: ClientDiscountRootOwner['kind'];
  rootId?: number;
  ruleId?: number;
  target?: ClientTargetPath;
};

export function ClientDiscountRootsEditor({
  type,
  url,
  sections,
  readOnly,
  refresh,
}: {
  type: ActivityType;
  url: string;
  sections: readonly ClientAgreementVendorTerms[];
  readOnly: boolean;
  refresh: () => Promise<void>;
}) {
  const [session, setSession] = useState<EditorSession | null>(null);
  const close = () => setSession(null);
  const mutations = clientDiscountMutations(url, refresh);
  const root = sections.find((root) => root.id === session?.rootId);
  const rule = root?.discount_rates.find((rule) => rule.id === session?.ruleId);
  const editRoot = (root: ClientAgreementVendorTerms) => {
    const rule = root.discount_rates.find(
      (rule) => rule.target_path.length === 1 && !rule.target_activity && !rule.target_category,
    );
    setSession({
      kind: root.owner.kind,
      rootId: root.id,
      ruleId: rule?.id,
      target: clientDiscountOwner(root.owner).rootPath,
    });
  };
  return (
    <>
      {(['vendor', 'service-category'] as const).map((kind) => {
        const family = sections.filter((root) => root.owner.kind === kind);
        const label = kind === 'vendor' ? 'Media' : 'Services';
        return (
          <Box key={kind} as="section" aria-label={`${label} discounts`} border="default" borderRadius="md" mb="4">
            <Box display="flex" alignItems="center" gap="3" p="3" fontWeight="semibold">
              {label}
              {!readOnly && (
                <Button variant="secondary" size="sm" icon="plus" ml="auto" onClick={() => setSession({ kind })}>
                  Add rule
                </Button>
              )}
            </Box>
            {family.length ? (
              <ClientDiscountTree
                activeType={type}
                kind={kind}
                roots={family}
                compact
                readOnly={readOnly}
                onAddRule={(root) => setSession({ kind, rootId: root.id })}
                onEditRoot={editRoot}
                onEditRule={(root, rule) => setSession({ kind, rootId: root.id, ruleId: rule.id })}
                onReorderRules={async (root, target, ids) => {
                  try {
                    await mutations.reorderRules(root.id, target, ids);
                  } catch (error) {
                    actionErrorOrThrow(error as never);
                  }
                }}
              />
            ) : (
              <Box p="4" color="secondary" textSize="sm">
                No {label.toLowerCase()} discounts yet.
              </Box>
            )}
          </Box>
        );
      })}
      {!readOnly && session && (
        <ClientRuleDialog
          open
          activeType={type}
          kind={session.kind}
          owner={root ? clientDiscountOwner(root.owner) : undefined}
          draft={{
            ...(rule ? formValuesFromRule(rule) : { ...emptyRuleValues(), target_path: session.target ?? null }),
            provider: root?.provider ?? null,
          }}
          editingRuleId={rule?.id}
          onSubmit={async (input) => {
            await mutations.saveRule(input, rule?.id, root?.id);
            close();
          }}
          onRemove={
            rule
              ? async () => {
                  await mutations.removeRule(rule.id);
                  close();
                }
              : root && session.target && root.discount_rates.length === 0
                ? async () => {
                    await mutations.removeRoot(root.id);
                    close();
                  }
                : undefined
          }
          removeLabel={rule ? 'Delete rule' : 'Delete root'}
          onClose={close}
        />
      )}
    </>
  );
}
